import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuCalendarCheck, LuChevronDown, LuPlus } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { useTranslation } from '../../../i18n/LanguageContext';
import { dateKey } from './calendarDates';
import { JobCard, LeaveBanner, VisitCard } from './CalendarEntries';

// Penceresi açık vizitlerden ilk bu kadarı gösterilir
const WINDOW_PREVIEW = 3;

/**
 * One day, split the way people plan it: first what is planned for the day
 * (visits on their visit day or done that day, and jobs), then the visits
 * whose window is open on it and could also be done that day. Leave first.
 */
const DayAgenda = ({ date, entries, windowVisits, leaves, onOpenVisit, onOpenJob, onAddVisit, onAddJob, projectSlotOf, projectNameOf, heading = true, showEmpty = true, canAdd = true, showNotes = false }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const [showAllWindows, setShowAllWindows] = useState(false);
  const dayKey = dateKey(date);
  const isToday = dayKey === dateKey(new Date());
  const total = entries.length + windowVisits.length;

  useEffect(() => {
    setShowAllWindows(false);
  }, [dayKey]);

  const visitCard = (visit, subtle) => (
    <VisitCard
      key={`v-${visit.id}`}
      visit={visit}
      date={date}
      projectName={projectNameOf(visit.projectId)}
      projectSlot={projectSlotOf(visit.projectId)}
      onOpen={onOpenVisit}
      subtle={subtle}
      showNotes={showNotes}
    />
  );

  const shownWindows = showAllWindows ? windowVisits : windowVisits.slice(0, WINDOW_PREVIEW);

  return (
    <section aria-label={date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })} className="tw-space-y-3">
      {heading && (
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
          <h3 className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
            {date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
            {isToday && <span className="tw-ml-2 tw-rounded-full tw-bg-teal-500/15 tw-px-2 tw-py-0.5 tw-align-middle tw-text-[11px] tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300">{label('today')}</span>}
          </h3>
          {total > 0 && <span className="tw-shrink-0 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('entryCount', { count: total })}</span>}
        </div>
      )}

      <LeaveBanner leaves={leaves} />

      {entries.length > 0 && (
        <div className="tw-space-y-2">
          {windowVisits.length > 0 && <p className="tw-px-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.1em] tw-text-slate-500 dark:tw-text-slate-400">{label('agenda.planned')}</p>}
          <ul className="tw-space-y-2">
            {entries.map((entry) => (entry.kind === 'visit'
              ? visitCard(entry.item, false)
              : <JobCard key={`j-${entry.item.id}`} job={entry.item} date={date} onOpen={onOpenJob} showNotes={showNotes} />))}
          </ul>
        </div>
      )}

      {entries.length === 0 && windowVisits.length > 0 && (
        <p className="tw-rounded-xl tw-bg-slate-500/[0.06] tw-px-3 tw-py-2 tw-text-xs tw-text-slate-500 dark:tw-bg-white/[0.04] dark:tw-text-slate-400">{label('agenda.nothingPlanned')}</p>
      )}

      {windowVisits.length > 0 && (
        <div className="tw-space-y-2">
          <div className="tw-px-0.5">
            <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.1em] tw-text-slate-500 dark:tw-text-slate-400">{label('agenda.openWindows', { count: windowVisits.length })}</p>
            <p className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{label('agenda.openWindowsHint')}</p>
          </div>
          <ul className="tw-space-y-1.5">{shownWindows.map((visit) => visitCard(visit, true))}</ul>
          {windowVisits.length > WINDOW_PREVIEW && (
            <button
              type="button"
              onClick={() => setShowAllWindows(!showAllWindows)}
              aria-expanded={showAllWindows}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-transparent tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-teal-700 hover:tw-bg-teal-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-300"
            >
              <LuChevronDown aria-hidden="true" className={`tw-h-3.5 tw-w-3.5 tw-transition-transform ${showAllWindows ? 'tw-rotate-180' : ''}`} />
              {showAllWindows ? label('agenda.showLess') : label('agenda.showAll', { count: windowVisits.length })}
            </button>
          )}
        </div>
      )}

      {total === 0 && showEmpty && (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-300 tw-px-4 tw-py-6 tw-text-center dark:tw-border-white/15">
          <LuCalendarCheck aria-hidden="true" className="tw-h-7 tw-w-7 tw-text-slate-300 dark:tw-text-slate-600" />
          <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('emptyDay')}</p>
          {canAdd && (
          <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
            <Button size="sm" variant="primary" onClick={() => onAddVisit(date)}>
              <LuPlus aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {label('actions.addVisit')}
            </Button>
            <Button size="sm" onClick={() => onAddJob(date)}>
              <LuBriefcase aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {label('actions.addJob')}
            </Button>
          </div>
          )}
        </div>
      )}
    </section>
  );
};

DayAgenda.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  entries: PropTypes.arrayOf(PropTypes.shape({ kind: PropTypes.string, item: PropTypes.object })).isRequired,
  windowVisits: PropTypes.arrayOf(PropTypes.object),
  leaves: PropTypes.arrayOf(PropTypes.object).isRequired,
  onOpenVisit: PropTypes.func.isRequired,
  onOpenJob: PropTypes.func.isRequired,
  onAddVisit: PropTypes.func,
  onAddJob: PropTypes.func,
  projectSlotOf: PropTypes.func.isRequired,
  projectNameOf: PropTypes.func.isRequired,
  heading: PropTypes.bool,
  showEmpty: PropTypes.bool,
  // Operasyon Yönetimi başka bir çalışanın takvimine bakar; oradan ekleme yapılmaz
  canAdd: PropTypes.bool,
  // Vizit notu ve iş açıklaması kartlarda gösterilir
  showNotes: PropTypes.bool,
};

DayAgenda.defaultProps = { windowVisits: [] };

export default DayAgenda;
